import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DollarSign, AlertCircle, FileText, CheckCircle2, Plus, UserPlus, Bell, ArrowUpRight, Receipt, Send, Sparkles, MailOpen, FileCheck2, FileX2, MailCheck, MousePointerClick, MailWarning } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import JobCalendar from "@/components/JobCalendar";
import { useNotifications } from "@/context/NotificationsContext";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const KPI = ({ label, value, hint, icon: Icon, tid, tone = "blue" }) => (
  <div data-testid={tid} className="bg-white border border-slate-200 rounded-md p-5 hover:border-slate-300 transition-colors">
    <div className="flex items-start justify-between">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center bg-${tone}-50 text-${tone}-600 border border-${tone}-100`}>
        <Icon className="w-4 h-4" strokeWidth={2.4} />
      </div>
      <ArrowUpRight className="w-4 h-4 text-slate-300" />
    </div>
    <div className="mt-4 font-display text-3xl font-extrabold text-slate-900 tracking-tight">{value}</div>
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mt-1">{label}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

const activityStyle = (t) => {
  switch (t) {
    case "quote_opened": return { Icon: MailOpen, bg: "#eff6ff", fg: "#1d4ed8" };
    case "quote_accepted": return { Icon: FileCheck2, bg: "#ecfdf5", fg: "#047857" };
    case "quote_rejected": return { Icon: FileX2, bg: "#fef2f2", fg: "#b91c1c" };
    case "quote_clicked": return { Icon: MousePointerClick, bg: "#e0f2fe", fg: "#0369a1" };
    case "quote_delivered": return { Icon: MailCheck, bg: "#ecfeff", fg: "#0e7490" };
    case "email_bounced": return { Icon: MailWarning, bg: "#fef2f2", fg: "#b91c1c" };
    case "new_customer": return { Icon: UserPlus, bg: "#f5f3ff", fg: "#6d28d9" };
    case "test": return { Icon: Sparkles, bg: "#fef9c3", fg: "#854d0e" };
    default: return { Icon: Bell, bg: "#f1f5f9", fg: "#334155" };
  }
};

const fmtAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
};

const RecentActivityHeader = () => {
  const nav = useNavigate();
  return (
    <button onClick={() => nav("/dashboard/notifications")} className="text-[11px] font-bold uppercase tracking-wider text-blue-600 hover:text-blue-700" data-testid="dash-activity-see-all">
      See all →
    </button>
  );
};

const RecentActivityWidget = () => {
  const { items } = useNotifications();
  const nav = useNavigate();
  const relevant = items.filter((n) => ["quote_opened", "quote_clicked", "quote_delivered", "email_bounced", "quote_accepted", "quote_rejected", "new_customer"].includes(n.type)).slice(0, 8);
  if (relevant.length === 0) {
    return <div className="mt-3 text-sm text-slate-500" data-testid="dash-activity-empty">Nothing yet. Customers' quote opens and acceptances will show up here in real time.</div>;
  }
  return (
    <ul className="mt-3 space-y-2" data-testid="dash-activity-list">
      {relevant.map((n) => {
        const s = activityStyle(n.type);
        const Icon = s.Icon;
        return (
          <li key={n.id} className="flex items-start gap-3 p-2 -mx-2 rounded-md hover:bg-slate-50 cursor-pointer" onClick={() => n.quote_id ? nav(`/dashboard/quotes/${n.quote_id}`) : nav("/dashboard/notifications")} data-testid={`dash-activity-${n.id}`}>
            <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: s.bg, color: s.fg }}>
              <Icon className="w-3.5 h-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{n.title}</div>
              <div className="text-xs text-slate-600 truncate">{n.message}</div>
            </div>
            <div className="text-[10px] text-slate-400 shrink-0 mt-1">{fmtAgo(n.created_at)}</div>
          </li>
        );
      })}
    </ul>
  );
};

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [limits, setLimits] = useState(null);

  const load = async () => {
    const [s, a, c, l] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/activity"),
      api.get("/dashboard/recent-customers"),
      api.get("/subscription/limits"),
    ]);
    setStats(s.data); setActivity(a.data); setCustomers(c.data); setLimits(l.data);
  };
  useEffect(() => { load(); }, []);

  const upgrade = async () => {
    const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  };

  return (
    <div data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Everything you need to run the shop today.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="btn-new-quote" onClick={() => nav("/dashboard/quotes/new")} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="w-4 h-4 mr-1.5" /> New Quote
          </Button>
          <Button data-testid="btn-new-invoice" onClick={() => nav("/dashboard/quotes")} variant="outline">
            <Receipt className="w-4 h-4 mr-1.5" /> New Invoice
          </Button>
          <Button data-testid="btn-add-customer" onClick={() => nav("/dashboard/customers")} variant="outline">
            <UserPlus className="w-4 h-4 mr-1.5" /> Add Customer
          </Button>
          <Button data-testid="btn-reminder" onClick={() => nav("/dashboard/invoices")} variant="outline">
            <Bell className="w-4 h-4 mr-1.5" /> Send Reminder
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <JobCalendar />
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI tid="kpi-quotes-sent" label="Quotes Sent" value={fmt(stats?.quotes_sent_amount)} icon={Send} tone="blue" hint="Total $ out the door" />
        <KPI tid="kpi-outstanding" label="Outstanding" value={fmt(stats?.outstanding)} icon={AlertCircle} tone="amber" hint="Unpaid invoices" />
        <KPI tid="kpi-revenue" label="Total Revenue" value={fmt(stats?.total_revenue)} icon={DollarSign} tone="emerald" hint={`${fmt(stats?.revenue_month)} this month`} />
        <KPI tid="kpi-quotes-accepted" label="Quotes Accepted" value={stats?.quotes_accepted ?? 0} icon={CheckCircle2} tone="emerald" hint="All time" />
      </div>

      {limits && !limits.is_pro && (
        <div className="mt-6 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-sky-50 p-5 flex flex-wrap items-center gap-4 justify-between" data-testid="free-plan-banner">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-extrabold text-slate-900">
                Free plan · <span data-testid="quote-usage">{limits.quotes_used} of {limits.quotes_limit}</span> free quotes used
              </div>
              <div className="text-sm text-slate-600 mt-0.5">
                {limits.quotes_remaining > 0
                  ? <>You have <b>{limits.quotes_remaining}</b> free {limits.quotes_remaining === 1 ? "quote" : "quotes"} left. After that, upgrade to Pro to keep going — unlimited quotes + full customer database.</>
                  : <>You've used all 3 free quotes. Upgrade to Pro to create your next one — unlimited quotes, $3.99/mo.</>}
              </div>
            </div>
          </div>
          <Button data-testid="banner-upgrade" onClick={upgrade} className="bg-blue-600 hover:bg-blue-700 text-white">
            Upgrade to Pro · $3.99/mo
          </Button>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Revenue Trend</div>
              <div className="font-display text-lg font-bold text-slate-900">Last 6 months</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={stats?.chart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} formatter={(v) => fmt(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3, fill: "#2563eb" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Recent Activity</div>
              <div className="font-display text-lg font-bold text-slate-900">What's happening</div>
            </div>
            <RecentActivityHeader />
          </div>
          <RecentActivityWidget />
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Recent Customers</div>
            <div className="font-display text-lg font-bold text-slate-900">Newly added</div>
          </div>
          <Button variant="outline" onClick={() => nav("/dashboard/customers")} data-testid="view-all-customers">View all</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-customers-table">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Phone</th>
                <th className="text-left px-5 py-3">Address</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-6 text-center text-slate-500">
                  No customers yet. <button onClick={() => nav('/customers')} className="text-blue-600 font-medium">Add your first</button>
                </td></tr>
              )}
              {customers.map((c) => (
                <tr key={c.id} className="odd:bg-slate-50/50 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/dashboard/customers/${c.id}`)} data-testid={`dash-customer-${c.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-5 py-3 text-slate-600">{c.email || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{c.phone || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{c.address || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
